import React from "react";
import get from "lodash/get";
import { connect } from "react-redux";
import moment from "moment-timezone";
import {
  Alert,
  Icon,
  Layout,
  Table,
  Badge,
  Button,
  Modal,
  Switch,
  message,
  Tooltip,
} from "antd";
import { SyncOutlined } from "@ant-design/icons";
import SimulationForm from "../../components/SimulationForm/SimulationForm";
import classes from "./Simulation.css";

import isAuthenticated from "../../utils/isAuthenticated";
import {
  getSimulationRequest,
  deleteSimulationRequest,
  activateSimulationRequest,
} from "../../api/requests";
import { logout } from "../../reducers/user/actions";
import {
  getSimulationCreatePermission,
  getSimulationUpdatePermission,
  getSimulationDeletePermission,
  getUserTimezone,
  getUserShortTimezone,
} from "../../reducers/user/selectors";
import { withNamespaces } from "react-i18next";
import FileLink from "../../components/FileLink/FileLink";

const { Content } = Layout;

const statusColors = {
  not_started: "#777",
  ready: "#c4c400",
  in_progress: "#c4c400",
  completed: "#14950b",
};

class Simulation extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      simulationList: [],
      errorMessage: "",
      responseMessage: "",
      simulationEdit: null,
    };
  }

  async componentDidMount() {
    this.getSimulationList();
  }

  getSimulationList = async () => {
    this.setState({
      errorMessage: null,
      isLoading: true,
      responseMessage: null,
    });

    try {
      const { data } = await getSimulationRequest();
      this.setState({ simulationList: data });
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      this.setState({
        subject: null,
        errorMessage: get(e, "response.data.error", "Error"),
      });
    }

    this.setState({ isLoading: false });
  };

  columns = [
    {
      dataIndex: "name",
      key: "name",
      title: this.props.i18n.t("Name"),
      render: (val, record) => (
        <span>
          {val} &nbsp;{" "}
          <Badge
            count={record.state}
            style={{
              backgroundColor: record.state === "active" ? "#52c41a" : "#777",
            }}
          />
        </span>
      ),
    },
    {
      dataIndex: "simulation_date",
      key: "simulation_date",
      title: this.props.i18n.t("NamespaceSimulation:Simulation date"),
      render: (val) => (
        <span>
          {moment
            .utc(val)
            .tz(this.props.userTimezone || "UTC")
            .format("YYYY-MM-DD HH:mm")}{" "}
          &nbsp;{this.props.userShortTimezone || "UTC"}
        </span>
      ),
    },
    {
      dataIndex: "subject_count",
      key: "subject_count",
      title: this.props.i18n.t("NamespaceSimulation:Number of patients"),
    },
    {
      dataIndex: "simulation_count",
      key: "simulation_count",
      title: this.props.i18n.t("NamespaceSimulation:Number of Simulations"),
    },
    {
      dataIndex: "min_age",
      key: "min_age",
      title: this.props.i18n.t("Min age"),
    },
    {
      dataIndex: "max_age",
      key: "max_age",
      title: this.props.i18n.t("Max age"),
    },
    {
      dataIndex: "status",
      key: "status",
      title: this.props.i18n.t("Status"),
      render: (val) => (
        <Badge
          count={val.replace(/_/g, " ")}
          style={{ backgroundColor: statusColors[val] }}
        />
      ),
    },
    {
      dataIndex: "id",
      key: "id",
      title: this.props.i18n.t("Actions"),
      render: (val, simulation) => (
        <div className={classes.actions}>
          <Tooltip
            placement="topLeft"
            title={this.props.i18n.t("NamespaceSimulation:Edit simulation")}
          >
            <Icon
              type="edit"
              theme="filled"
              className={classes.actionIcon}
              onClick={this.onSimulationEdit(simulation)}
            />
          </Tooltip>

          {simulation.link && <FileLink url={simulation.link} />}

          <div>
            <b></b>
            <br />
            <Tooltip
              placement="topLeft"
              title={this.props.i18n.t(
                "NamespaceSimulation:Activate / Deactivate simulation"
              )}
            >
              <Switch
                disabled={
                  (simulation.state === "active" &&
                    !this.props.simulationDeletePermission) ||
                  (simulation.state === "inactive" &&
                    !this.props.simulationUpdatePermission)
                }
                className={classes.activateBtn}
                checked={simulation.state === "active"}
                onChange={this.onSwitchSimulationActivation(
                  simulation.id,
                  simulation.state
                )}
              />
            </Tooltip>
          </div>
        </div>
      ),
    },
  ];

  onSwitchSimulationActivation = (id, state) => async () => {
    try {
      if (state === "active") {
        await deleteSimulationRequest(id);
      } else {
        await activateSimulationRequest(id);
      }

      message.success(this.props.i18n.t("Saved"));

      this.getSimulationList();
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      message.error(get(e, "response.data.error", "Error"));
      console.log(e);
    }
  };

  onSimulationEdit = (simulation) => () => {
    this.setState({ simulationEdit: simulation });
  };

  onSimulationEditHide = () => {
    this.setState({ simulationEdit: null });
  };

  onSimulationEditSubmit = () => {
    this.setState({ simulationEdit: null }, this.getSimulationList);
  };

  addSimulation = () => {
    this.setState({ simulationEdit: {} });
  };

  render() {
    const {
      simulationList,
      errorMessage,
      responseMessage,
      isLoading,
      simulationEdit,
    } = this.state;
    const { simulationCreatePermission } = this.props;

    return (
      <div style={{ backgroundColor: "#FAFAFA" }}>
        <Layout className={classes.parentContainer}>
          <Content className={classes.content}>
            <h3 className="headline-default">
              {this.props.i18n.t("NamespaceSimulation:Simulation management")}
            </h3>
            <br />

            {simulationCreatePermission && (
              <Button type="primary" onClick={this.addSimulation}>
                {this.props.i18n.t("NamespaceSimulation:Add simulation")}
              </Button>
            )}
            {"  "}
            <Tooltip title={this.props.i18n.t("Refresh")}>
              <Button
                type="primary"
                onClick={this.getSimulationList}
                shape="circle"
              >
                <SyncOutlined spin={isLoading} />
              </Button>
            </Tooltip>
            <br />
            <br />

            {errorMessage && <Alert type="error" message={errorMessage} />}

            {responseMessage && (
              <Alert type="success" message={responseMessage} />
            )}

            <Table
              columns={this.columns}
              dataSource={simulationList}
              loading={isLoading}
              rowKey="id"
              pagination={false}
            />

            <Modal
              visible={!!simulationEdit}
              onCancel={this.onSimulationEditHide}
              width="560px"
              footer={null}
            >
              {simulationEdit && (
                <SimulationForm
                  {...simulationEdit}
                  isNew={simulationEdit && !simulationEdit.id}
                  onSubmit={this.onSimulationEditSubmit}
                  onCancel={this.onSimulationEditHide}
                />
              )}
            </Modal>
          </Content>
        </Layout>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userTimezone: getUserTimezone(state),
  userShortTimezone: getUserShortTimezone(state),
  simulationCreatePermission: getSimulationCreatePermission(state),
  simulationDeletePermission: getSimulationDeletePermission(state),
  simulationUpdatePermission: getSimulationUpdatePermission(state),
});

const mapDispatchToProps = { logout };

export default isAuthenticated(
  connect(mapStateToProps, mapDispatchToProps)(withNamespaces()(Simulation))
);



// WEBPACK FOOTER //
// ./src/containers/Simulation/Simulation.js